import type {
  SpyBrand,
  SpyAd,
  AdsFiltersData,
  AdsPagination,
  AdsQueryParams,
  CreateBrandPayload,
  UpdateBrandPayload,
} from "../types/adsSpy.types";
import { axiosInstanceWebsite } from "@/services/instance";

export const adsSpyService = {
  // ── Brands ──
  async getBrands(): Promise<SpyBrand[]> {
    const { data } = await axiosInstanceWebsite.get("/ads/spy/brands");
    return data;
  },

  async createBrand(payload: CreateBrandPayload): Promise<SpyBrand> {
    const { data } = await axiosInstanceWebsite.post("/ads/spy/brands", payload);
    return data;
  },

  async updateBrand(id: number, payload: UpdateBrandPayload): Promise<SpyBrand> {
    const { data } = await axiosInstanceWebsite.put(`/ads/spy/brands/${id}`, payload);
    return data;
  },

  async deleteBrand(id: number): Promise<void> {
    await axiosInstanceWebsite.delete(`/ads/spy/brands/${id}`);
  },

  // ── Ads ──
  async getAds(params: AdsQueryParams): Promise<{
    data: SpyAd[];
    pagination: AdsPagination;
  }> {
    const { data } = await axiosInstanceWebsite.get("/ads/spy/ads", { params });
    return { data: data.data, pagination: data.pagination };
  },

  async getAd(id: number): Promise<SpyAd> {
    const { data } = await axiosInstanceWebsite.get(`/ads/spy/ads/${id}`);
    return data;
  },

  async toggleBookmark(id: number): Promise<boolean> {
    const { data } = await axiosInstanceWebsite.patch(`/ads/spy/ads/${id}/bookmark`);
    return data.is_bookmarked;
  },

  async getAiFilters(brandId?: string): Promise<AdsFiltersData> {
    const params = brandId ? { brand_id: brandId } : {};
    const { data } = await axiosInstanceWebsite.get("/ads/spy/ads/filters", { params });
    return data;
  },

  // ── Scraping ──
  async scrapeBrand(
    brandId: number,
    limit = 20
  ): Promise<{ status: string }> {
    const { data } = await axiosInstanceWebsite.post(`/ads/spy/scrape/${brandId}`, { limit });
    return data;
  },

  async getScrapeStatus(brandId: number): Promise<{
    brand_id: number;
    brand_name: string;
    last_scraped_at: string | null;
    total_ads: number;
    status: string;
  }> {
    const { data } = await axiosInstanceWebsite.get(`/ads/spy/scrape/status/${brandId}`);
    return data;
  },

  async scrapeAll(limit = 20) {
    const { data } = await axiosInstanceWebsite.post("/ads/spy/scrape/all", { limit });
    return data;
  },

  // ── AI Analysis ──
  async analyzeAd(adId: number): Promise<{ data: Record<string, unknown>; cached: boolean }> {
    const { data } = await axiosInstanceWebsite.post(`/ads/spy/ads/analyze/${adId}`, {});
    return { data: data, cached: false };
  },

  // ── Image Reupload ──
  async reuploadImage(url: string): Promise<{ s3_url: string }> {
    const { data } = await axiosInstanceWebsite.post("/ads/spy/reupload-image", { url });
    return data;
  },
};
